import { useEffect, useRef, type RefObject } from "react";
import { pushHisDebugLog } from "../utils/hisDebugLog";
import {
  setLayoutDebugData,
  sliceElement,
  type LayoutDebugData,
} from "../utils/layoutDebugSnapshot";

function readMemory():
  | { usedJSHeapSize?: number; totalJSHeapSize?: number; jsHeapSizeLimit?: number }
  | undefined {
  const perf = globalThis.performance as Performance & {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  };
  if (!perf?.memory) return undefined;
  return {
    usedJSHeapSize: perf.memory.usedJSHeapSize,
    totalJSHeapSize: perf.memory.totalJSHeapSize,
    jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
  };
}

/**
 * 单店页等：周期性采样左侧滚动区与内容根节点尺寸；scrollHeight 明显变化时写入环形日志。
 */
export function useHisLayoutDebugSampling(
  enabled: boolean,
  leftScrollRef: RefObject<HTMLElement | null>,
  storeContentRef: RefObject<HTMLElement | null>,
): void {
  const prevScrollHRef = useRef<number | null>(null);
  const prevStoreScrollHRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setLayoutDebugData(null);
      return undefined;
    }

    const tick = (): void => {
      const path = globalThis.location?.pathname ?? "";
      const left = leftScrollRef.current;
      const store = storeContentRef.current;

      const data: LayoutDebugData = {
        ts: new Date().toISOString(),
        path,
        windowInner: {
          width: window.innerWidth,
          height: window.innerHeight,
          dpr: window.devicePixelRatio || 1,
        },
        documentElement: {
          clientWidth: document.documentElement.clientWidth,
          clientHeight: document.documentElement.clientHeight,
        },
        memory: readMemory(),
        leftScroll: left ? sliceElement(left, "left-scroll") : undefined,
        storeContent: store ? sliceElement(store, "store-content") : undefined,
      };
      setLayoutDebugData(data);

      if (left) {
        const sh = left.scrollHeight;
        const prev = prevScrollHRef.current;
        if (prev != null && Math.abs(sh - prev) >= 12) {
          pushHisDebugLog(
            `layout:leftScroll scrollHeight ${prev}→${sh} (clientH=${left.clientHeight})`,
          );
        }
        prevScrollHRef.current = sh;
      } else {
        prevScrollHRef.current = null;
      }

      if (store) {
        const sh = store.scrollHeight;
        const prev = prevStoreScrollHRef.current;
        if (prev != null && Math.abs(sh - prev) >= 12) {
          pushHisDebugLog(
            `layout:storeContent scrollHeight ${prev}→${sh} (offsetH=${store.offsetHeight})`,
          );
        }
        prevStoreScrollHRef.current = sh;
      } else {
        prevStoreScrollHRef.current = null;
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1200);
    const onResize = () => tick();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        pushHisDebugLog(
          `visibility:visible (hidden was ${document.hidden})`,
        );
      }
      tick();
    };
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVisibility);
      setLayoutDebugData(null);
    };
  }, [enabled, leftScrollRef, storeContentRef]);
}
